import { useEffect, useRef, useState } from "react";
import CreatorAvatar from "./CreatorAvatar";

function normalizeCreator(raw) {
  if (!raw || typeof raw !== "object") return null;
  const code = String(raw.code || "").trim();
  const first = String(raw.first_name || "").trim();
  const last = String(raw.last_name || "").trim();
  const name = [first, last].filter(Boolean).join(" ") || code || "Unknown";
  return {
    id: code || `${first}-${last}`,
    code,
    name,
    handle: code ? `@${code}` : "",
    logo: typeof raw.logo === "string" ? raw.logo : "",
  };
}
const DEFAULT_API_BASE_URL = "https://app.isonic.ai/api";

function getApiBaseUrl() {
  const raw = process.env.REACT_APP_API_URL || DEFAULT_API_BASE_URL;
  return raw.replace(/\/$/, "");
}

const apiBaseUrl = getApiBaseUrl();

function getCardStep(el) {
  const card = el?.querySelector("[data-creator-card]");
  if (!card) return 220;
  const styles = window.getComputedStyle(el);
  const gap = parseFloat(styles.columnGap || styles.gap || "0") || 0;
  return card.getBoundingClientRect().width + gap;
}

export default function CreatorsAccordion({
  className = "",
  defaultOpen = true,
}) {
  const [open, setOpen] = useState(() => defaultOpen);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [creators, setCreators] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [autoScrollPaused, setAutoScrollPaused] = useState(false);

  const scrollerRef = useRef(null);
  const inputRef = useRef(null);
  const requestIdRef = useRef(0);

  const toggle = () => {
    setOpen((prev) => {
      const next = !prev;
      if (next) {
        setTimeout(() => inputRef.current?.focus(), 220);
      } else {
        setQuery("");
        setDebouncedQuery("");
      }
      setAutoScrollPaused(false);
      return next;
    });
  };

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (!open) return;

    const id = ++requestIdRef.current;
    setLoading(true);
    setError(null);

    const url = debouncedQuery
      ? `${apiBaseUrl}/public/creators?search=${encodeURIComponent(debouncedQuery)}`
      : `${apiBaseUrl}/public/creators`;

    (async () => {
      try {
        const res = await fetch(url, {
          headers: { Accept: "application/json" },
        });
        const data = await res.json();
        if (id !== requestIdRef.current) return;

        if (!res.ok) {
          throw new Error(data?.error || `Request failed (${res.status})`);
        }

        const list = Array.isArray(data?.creators)
          ? data.creators.map(normalizeCreator).filter(Boolean)
          : [];
        setCreators(list);
        setMeta(data?.meta || null);
      } catch (e) {
        if (id !== requestIdRef.current) return;
        setError(e?.message || "Couldn't load creators");
        setCreators([]);
        setMeta(null);
      } finally {
        if (id !== requestIdRef.current) return;
        setLoading(false);
      }
    })();
  }, [open, debouncedQuery]);

  // Reset scroll position when the result set changes so the user always
  // sees the first match.
  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollLeft = 0;
  }, [creators]);

  const scrollByCard = (direction) => {
    const el = scrollerRef.current;
    if (!el) return;
    const step = getCardStep(el);
    el.scrollBy({ left: direction * step * 2, behavior: "smooth" });
  };

  const pauseAutoScroll = () => {
    setAutoScrollPaused(true);
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTo({ left: el.scrollLeft, behavior: "auto" });
  };

  const resumeAutoScroll = () => {
    setAutoScrollPaused(false);
  };

  useEffect(() => {
    if (!open || loading || creators.length <= 1 || autoScrollPaused) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const intervalId = window.setInterval(() => {
      const el = scrollerRef.current;
      if (!el) return;

      const maxScrollLeft = el.scrollWidth - el.clientWidth;
      if (maxScrollLeft <= 0) return;

      const step = getCardStep(el);
      if (el.scrollLeft >= maxScrollLeft - step / 2) {
        el.scrollTo({ left: 0, behavior: "auto" });
        return;
      }

      el.scrollBy({ left: step, behavior: "smooth" });
    }, 2600);

    return () => window.clearInterval(intervalId);
  }, [autoScrollPaused, creators.length, loading, open]);

  const countLabel = (() => {
    if (loading && creators.length === 0) return "loading…";
    const shown = creators.length;
    const total = meta?.total_count;
    if (typeof total === "number" && total !== shown) {
      return `${shown} of ${total}`;
    }
    return `${shown}`;
  })();

  return (
    <div className={`min-w-0 max-w-full ${className}`.trim()}>
      <div className="flex justify-center">
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          aria-controls="creators-accordion-panel"
          className="group inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-[14px] font-medium text-ink-500 transition hover:text-ink-900 md:py-1 md:text-[13px]"
        >
          <span>{open ? "Hide creators" : "Browse creators"}</span>
          <svg
            viewBox="0 0 24 24"
            width="12"
            height="12"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.25"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
            className={`transition-transform duration-200 ${
              open ? "rotate-180" : ""
            }`}
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        </button>
      </div>

      <div
        id="creators-accordion-panel"
        aria-hidden={!open}
        className={`creators-accordion ${open ? "open" : ""}`}
      >
        <div className="creators-accordion-inner">
          <div className="mt-3 min-w-0 overflow-hidden rounded-2xl border border-ink-200 bg-surface">
            <div className="flex items-center justify-between gap-3 px-4 pb-1 pt-4 md:px-4 md:pt-3.5">
              <div className="text-[15px] font-semibold text-ink-900 md:text-[14px]">
                Featured Creators
                <span className="ml-2 font-medium text-ink-400">
                  {countLabel}
                </span>
              </div>
              <label className="creators-search relative flex w-[min(15rem,58%)] items-center gap-2 rounded-full border border-ink-200 bg-ink-50 px-3 py-1.5 focus-within:border-ink-300 focus-within:bg-surface md:px-2.5 md:py-1">
                <span className="sr-only">Search creators</span>
                <span className="flex shrink-0 items-center text-ink-400">
                  <svg
                    viewBox="0 0 24 24"
                    width="14"
                    height="14"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                    className="block"
                  >
                    <circle cx="11" cy="11" r="7" />
                    <path d="m20 20-3.5-3.5" />
                  </svg>
                </span>
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search creators"
                  className="min-w-0 flex-1 border-0 bg-transparent p-0 text-[14px] leading-5 text-ink-900 outline-none placeholder:text-ink-300 md:text-[12.5px]"
                />
                {query ? (
                  <button
                    type="button"
                    onClick={() => setQuery("")}
                    aria-label="Clear search"
                    className="grid h-5 w-5 shrink-0 place-items-center rounded-full text-ink-400 hover:bg-ink-100 hover:text-ink-700"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      width="11"
                      height="11"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden
                      className="block"
                    >
                      <path d="M18 6 6 18" />
                      <path d="m6 6 12 12" />
                    </svg>
                  </button>
                ) : null}
              </label>
            </div>

            <div
              className="relative px-2 pb-5 pt-4 md:px-1 md:pb-4 md:pt-3"
              onMouseEnter={pauseAutoScroll}
              onMouseLeave={resumeAutoScroll}
            >
              <button
                type="button"
                onClick={() => scrollByCard(-1)}
                aria-label="Scroll left"
                className="absolute left-3 top-1/2 z-10 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full border border-ink-100 bg-surface text-ink-700 shadow-soft transition hover:bg-ink-50 md:left-2 md:h-8 md:w-8"
              >
                <svg
                  viewBox="0 0 24 24"
                  width="14"
                  height="14"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <path d="m15 18-6-6 6-6" />
                </svg>
              </button>
              <button
                type="button"
                onClick={() => scrollByCard(1)}
                aria-label="Scroll right"
                className="absolute right-3 top-1/2 z-10 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full border border-ink-100 bg-surface text-ink-700 shadow-soft transition hover:bg-ink-50 md:right-2 md:h-8 md:w-8"
              >
                <svg
                  viewBox="0 0 24 24"
                  width="14"
                  height="14"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <path d="m9 18 6-6-6-6" />
                </svg>
              </button>

              {error ? (
                <div className="px-4 py-8 text-center text-[14px] text-ink-500 md:text-[12.5px]">
                  Couldn&apos;t load creators.
                  <span className="ml-1 text-ink-400">({error})</span>
                </div>
              ) : loading && creators.length === 0 ? (
                <div
                  className="creators-scroller flex min-w-0 w-full max-w-full snap-x snap-mandatory gap-4 overflow-x-auto px-3 pb-2"
                  style={{ scrollbarWidth: "thin" }}
                >
                  {Array.from({ length: 6 }).map((_, i) => (
                    <CreatorCardSkeleton key={i} />
                  ))}
                </div>
              ) : creators.length === 0 ? (
                <div className="px-4 py-8 text-center text-[14px] text-ink-500 md:text-[12.5px]">
                  {debouncedQuery ? (
                    <>
                      No creators match{" "}
                      <span className="font-medium text-ink-700">
                        &ldquo;{debouncedQuery}&rdquo;
                      </span>
                      .
                    </>
                  ) : (
                    "No creators yet."
                  )}
                </div>
              ) : (
                <div
                  ref={scrollerRef}
                  className="creators-scroller flex min-w-0 w-full max-w-full snap-x snap-mandatory gap-4 overflow-x-auto px-3 pb-2"
                  style={{ scrollbarWidth: "thin" }}
                >
                  {creators.map((creator) => (
                    <CreatorCard key={creator.id} creator={creator} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CreatorCard({ creator }) {
  const href = creator.code
    ? `https://isonic.ai/${encodeURIComponent(creator.code)}`
    : null;

  const inner = (
    <>
      <CreatorAvatar
        name={creator.name}
        seed={creator.id || creator.code}
        logo={creator.logo}
        size={72}
        className="ring-2 ring-ink-100"
      />
      <div className="mt-2.5 line-clamp-1 text-[13px] font-semibold tracking-tight text-ink-900">
        {creator.name}
      </div>
      {creator.handle ? (
        <div className="line-clamp-1 text-[11.5px] text-ink-400">
          {creator.handle}
        </div>
      ) : null}
    </>
  );

  const className =
    "group flex w-[8.5rem] shrink-0 snap-start flex-col items-center rounded-2xl px-2 py-3 text-center transition duration-200 hover:bg-ink-50 sm:w-[9rem] md:w-36 md:px-2 md:py-2.5";

  if (!href) {
    return (
      <div data-creator-card className={className}>
        {inner}
      </div>
    );
  }

  return (
    <a
      data-creator-card
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title={`Open ${creator.name} on iSonic`}
      className={className}
    >
      {inner}
    </a>
  );
}

function CreatorCardSkeleton() {
  return (
    <div
      data-creator-card
      className="flex w-[8.5rem] shrink-0 snap-start flex-col items-center rounded-2xl px-2 py-3 sm:w-[9rem] md:w-36 md:px-2 md:py-2.5"
    >
      <div className="h-[72px] w-[72px] animate-pulse rounded-full bg-ink-100" />
      <div className="mt-3 h-3.5 w-24 animate-pulse rounded bg-ink-100" />
      <div className="mt-2 h-3 w-20 animate-pulse rounded bg-ink-50" />
    </div>
  );
}
